import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralPartnerApi, type PartnerAnalyticsReport } from '@/api/referralPartner';
import { useChainReportHospitalScope } from '@/hooks/useChainReportHospitalScope';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';

function defaultFromTo() {
  const to = new Date();
  const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

function pctLabel(v: number | null) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v.toFixed(1)}%`;
}

export default function PartnerAnalyticsPage() {
  const { from: defaultFrom, to: defaultTo } = useMemo(() => defaultFromTo(), []);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const { showHospitalFilter, hospitalOptions, selectedHospitalId, setSelectedHospitalId, isChainScoped } =
    useChainReportHospitalScope({ defaultScope: 'consolidated' });

  const hospitalIdParam =
    isChainScoped && selectedHospitalId && selectedHospitalId !== 'all' ? selectedHospitalId : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['partner-analytics', from, to, hospitalIdParam ?? 'all'],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.analytics({ from, to, hospitalId: hospitalIdParam });
      return (res.data?.data ?? null) as PartnerAnalyticsReport | null;
    },
    enabled: !!from && !!to,
  });

  const summary = data?.summary;
  const conversionChart = useMemo(() => {
    return (data?.conversionsByDay ?? []).map((d) => ({
      date: d.date.slice(5),
      OPD: d.opd,
      IPD: d.ipd,
    }));
  }, [data?.conversionsByDay]);

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Partner analytics</h1>
        <p className="text-gray-500">
          Partners added, referral leads, and OPD/IPD conversion trends for your scope.
        </p>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-xs text-gray-500">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-40" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-40" />
        </div>
        {showHospitalFilter && (
          <div className="min-w-[220px]">
            <Label className="text-xs text-gray-500">Hospital</Label>
            <Select value={selectedHospitalId || 'all'} onValueChange={setSelectedHospitalId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All hospitals" />
              </SelectTrigger>
              <SelectContent>
                {isChainScoped && <SelectItem value="all">All hospitals (chain)</SelectItem>}
                {hospitalOptions.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {(error as any)?.message || 'Failed to load analytics'}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Active partners</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{summary.activePartners}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">New links (range)</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{summary.newPartnerLinksInRange}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Referral leads</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{summary.referralLeads}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">OPD conversions</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{summary.opdConversions}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">IPD conversions</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{summary.ipdConversions}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Lead → OPD</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{pctLabel(summary.leadToOpdPct)}</CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Partners added per day</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.partnersAddedByDay ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => String(v).slice(5)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8B6914" name="New links" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Referral leads per day</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data?.leadsByDay ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => String(v).slice(5)} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#2563eb" name="Leads" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">OPD / IPD conversions per day</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={conversionChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="OPD" fill="#16a34a" />
                    <Bar dataKey="IPD" fill="#9333ea" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <section className="space-y-3">
            <h2 className="text-lg font-medium">Top performing partners</h2>
            <div className="rounded-lg border overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Partner</th>
                    <th className="px-4 py-2">Leads</th>
                    <th className="px-4 py-2">OPD</th>
                    <th className="px-4 py-2">IPD</th>
                    <th className="px-4 py-2">Points (approved)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(data?.topPartners ?? []).map((row) => (
                    <tr key={row.partnerId}>
                      <td className="px-4 py-2 font-medium">{row.name}</td>
                      <td className="px-4 py-2">{row.leads}</td>
                      <td className="px-4 py-2">{row.opd}</td>
                      <td className="px-4 py-2">{row.ipd}</td>
                      <td className="px-4 py-2">{row.points}</td>
                    </tr>
                  ))}
                  {!data?.topPartners?.length && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                        No partner activity in this range
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <p className="text-gray-500">No data for the selected range.</p>
      )}
    </div>
  );
}
